import React, { useCallback, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { useParams } from 'react-router-dom';
import { Box } from '@atlaskit/primitives';
import { errorNotificationBuilder, renderPromotionOption, useNotification } from '@css-xp/base';
import { abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';
import { ApplyPromoCodeForm } from './apply-promocode-form';
import { ApplyPromocodeField } from './apply-promocode-field';
import { useStoreActions } from '../../../../store';
import { CartPromotionAction } from '../types';
import { validateApplicablePromocode } from '../../../../queries/validate-promo-code';
import './apply-promocode-section.scss';

export const ApplyPromocodeSection = (Props: { productId: string; id: string; onClose: () => void }) => {
  const { productId, id, onClose } = Props;
  const { txaId } = useParams<{ txaId: string }>();
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [validPromotionData, setValidPromotionData] = useState<any>();
  const [promoCodeNotApplicableErrors, setPromoCodeNotApplicableErrors] = useState('');
  const { sendErrorNotification } = useNotification();

  const { updateEntitlementPromotions } = useStoreActions();
  const onSubmit = () => {
    updateEntitlementPromotions({
      identifier: id,
      promotion: validPromotionData,
      promotionAction: CartPromotionAction.ADD_APP_PROMOTION, //we are allowing only apps to add promocode
      isApp: true, //we are allowing only apps to add promocode
    });
    onClose();
  };

  const validatePromocode = async (promocode: string | undefined) => {
    try {
      const { data, errors } = await graphqlClient().query({
        query: validateApplicablePromocode,
        variables: {
          id: '',
          promoCodes: [
            {
              code: promocode,
              productKey: productId,
            },
          ],
          transactionAccountId: txaId,
        },
        fetchPolicy: 'no-cache',
      });
      if (errors) throw errors;
      if (data?.entitlementDetails?.promoCodesApplicable?.notApplicablePromotionCodesWithReason) {
        setIsValid(false);
        const validReason = data?.entitlementDetails?.promoCodesApplicable?.notApplicablePromotionCodesWithReason?.join(
          '.'
        );
        setPromoCodeNotApplicableErrors(validReason);
        return validReason;
      }
      const promoData = data?.entitlementDetails?.promoCodesApplicable?.promotions[0];
      setValidPromotionData({ ...promoData, promocode });
      setIsValid(true);
    } catch (error) {
      const notification = errorNotificationBuilder()
        .withHeader('We could not validate the promocode.')
        .withMessage(`Something is not right at our end. Please try again. If the issue persists, please contact us.`)
        .build();
      sendErrorNotification(notification);
    } finally {
      setIsValidating(false);
    }
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        validatePromocode(value);
      }, 1000),
    []
  );

  const validatePromocodeInput = useCallback(
    (e) => {
      const value = e.target.value;
      if (!value) return;
      setIsValidating(true);
      setIsValid(false);
      setValidPromotionData('');
      setPromoCodeNotApplicableErrors('');
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  return (
    <section>
      <Box padding="space.100">
        <ApplyPromoCodeForm className="add-promocode-form" onSubmit={onSubmit} onCancel={onClose} isValid={isValid}>
          <ApplyPromocodeField
            error={promoCodeNotApplicableErrors}
            isValid={isValid}
            isValidating={isValidating}
            onChange={validatePromocodeInput}
          />
          {validPromotionData && (
            <div className="promo-code-promotion-details">
              {renderPromotionOption(validPromotionData, { showBenefitNextLine: true })}
            </div>
          )}
        </ApplyPromoCodeForm>
      </Box>
    </section>
  );
};
